/*
 * THE PORTS' TYPES, as the type check of a `typescript` document reads them
 * (`CheckOptions.types`, Part 3 of docs/hierarchical-models-step-3-plan.md).
 * A module of its own with no imports, so that the model page can build its
 * check options without loading the language service.
 */

/** A port as the interface declares it: a name and a type, and for an input whether it is a list. */
interface DeclaredPort {
  name: string;
  type: string;
  isArray?: boolean;
}
/** The five port types as TypeScript, for a list of one of them; any other text is TypeScript already. */
const ELEMENT_TYPES: Readonly<Record<string, string>> = {
  BOOL: 'boolean',
  INT: 'number',
  DINT: 'number',
  REAL: 'number',
  STRING: 'string',
};

/** Each declared port's type, by name, as the type check reads it (`CheckOptions.types`): an array input is a list of its type. */
export function portTypes(
  iface: { inputs?: readonly DeclaredPort[]; outputs?: readonly DeclaredPort[] } | undefined,
): Record<string, string> {
  const types: Record<string, string> = {};
  for (const port of [...(iface?.inputs ?? []), ...(iface?.outputs ?? [])]) {
    const type = port.type.trim();
    if (!port.name || !type) continue;
    types[port.name] = port.isArray ? `(${ELEMENT_TYPES[type] ?? type})[]` : type;
  }
  return types;
}

/** A child's ports as the view gives them: a name, a declared type when it has one, and its wiring. */
interface ViewPort {
  name: string;
  type?: string;
  /** An input: the path that feeds it, '' when none does. */
  path?: string;
  /** An input: the constant that feeds it. */
  constant?: string;
}

/**
 * The ports that the platform gives each machine of an array (Step 4): `enabled` and `faulted`. The
 * same names as the host's `ARRAY_INPUTS` and `ARRAY_OUTPUTS`. This module has no imports, so it
 * does not load the host. A test pins the names to the host's.
 */
export const PLATFORM_PORTS: readonly string[] = ['enabled', 'faulted'];

/** The ports of a group's child as the check reads them (`ChildPorts`). */
interface CheckedChild {
  inputs: string[];
  outputs: string[];
  types: Record<string, string>;
  /** For an array child: its count, from 1. */
  count?: number;
  /** For an array child: the inputs that its wiring feeds, `enabled` among them when it is wired. */
  wired?: string[];
}

/**
 * A group's children as the check reads them (`CheckOptions.children`): each child's input and
 * output names, and their declared types. The root's document reads its children's outputs and
 * assigns their inputs, so without this the check takes `ps1.present` for a name nothing declares.
 *
 * An array child (Step 4) also gives its count and the inputs that its wiring feeds, as the host's
 * `portsOf` does. Its names leave out `enabled` and `faulted`, which the platform declares, as the
 * save's check does (model-runner `typeCheck.ts`, `withoutPlatform`). So the editor and the save
 * give the same reasons.
 */
export function childPorts(
  children: ReadonlyArray<{ name: string; count?: number; inputs: readonly ViewPort[]; outputs: readonly ViewPort[] }>,
): Record<string, CheckedChild> {
  return Object.fromEntries(
    children.map((c): [string, CheckedChild] => {
      const types = Object.fromEntries([...c.inputs, ...c.outputs].filter((p) => p.type).map((p) => [p.name, p.type as string]));
      if (c.count === undefined) return [c.name, { inputs: c.inputs.map((p) => p.name), outputs: c.outputs.map((p) => p.name), types }];
      const own = (p: ViewPort) => !PLATFORM_PORTS.includes(p.name);
      const wired = c.inputs.filter((p) => p.path || p.constant !== undefined).map((p) => p.name);
      return [
        c.name,
        {
          inputs: c.inputs.filter(own).map((p) => p.name),
          outputs: c.outputs.filter(own).map((p) => p.name),
          types,
          count: c.count,
          wired: [...new Set(wired)],
        },
      ];
    }),
  );
}
